import React, { useState } from 'react'
import api from '../lib/api'

export default function ChangePasswordModal({ isOpen, onClose }) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [submitted, setSubmitted] = useState(false)

  if (!isOpen) return null

  const isFieldMissing = (val) => submitted && !val

  const handleClose = () => {
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setError(null)
    setSuccess(null)
    setSubmitted(false)
    onClose()
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitted(true)
    setError(null)
    setSuccess(null)

    if (!currentPassword || !newPassword || !confirmPassword) return

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match')
      return
    }

    setSaving(true)
    try {
      const res = await api.patch('/change_password', {
        current_password: currentPassword,
        password: newPassword,
        password_confirmation: confirmPassword,
      })
      setSuccess(res.data.message)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err) {
      setError(err.response?.data?.errors?.join(', ') || 'Failed to change password')
    } finally {
      setSaving(false)
    }
  }

  const inputClass = "w-full border border-gray-400 rounded-md px-3 py-2.5 text-base sm:text-sm focus:ring-2 focus:ring-accent focus:border-accent"

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-surface border-[1.5px] border-ink shadow-lg w-full max-w-sm mx-4">
        <div className="px-6 py-4 border-b border-line">
          <h3 className="text-xl font-extrabold">Change Password</h3>
        </div>

        <div className="px-6 py-4">
          {error && (
            <div className="text-sm text-accent-dark bg-accent/10 border border-accent/40 p-3 mb-3">{error}</div>
          )}
          {success && (
            <div className="text-sm text-ink bg-surface p-3 rounded mb-3">{success}</div>
          )}

          {!success ? (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className={`${inputClass} ${isFieldMissing(currentPassword) ? 'border-accent' : ''}`}
                />
                {isFieldMissing(currentPassword) && <p className="text-xs text-accent-dark mt-1">Required</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={6}
                  className={`${inputClass} ${isFieldMissing(newPassword) ? 'border-accent' : ''}`}
                />
                {isFieldMissing(newPassword) && <p className="text-xs text-accent-dark mt-1">Required</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  minLength={6}
                  className={`${inputClass} ${isFieldMissing(confirmPassword) ? 'border-accent' : ''}`}
                />
                {isFieldMissing(confirmPassword) && <p className="text-xs text-accent-dark mt-1">Required</p>}
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm font-bold text-white bg-accent rounded-md hover:bg-accent-dark disabled:opacity-50"
                >
                  {saving ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          ) : (
            <div className="flex justify-end pt-2">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-bold text-white bg-accent rounded-md hover:bg-accent-dark"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
